/* eslint-disable complexity */
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import basketSelector from 'selectors/basket/basketSelector';
import preferredStoreInfoSelector from 'selectors/user/preferredStoreInfoSelector';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';
import { shipToHomeSelector } from 'selectors/page/product/fulfillmentOptions/deliveryOptions/shipToHome/shipToHomeSelector';
import { showReorderFulfillmentOptionsPdpSelector } from 'viewModel/selectors/testTarget/showReorderFulfillmentOptionsPdpSelector';
import { showReorderFulfillmentOptionsSelector } from 'viewModel/selectors/testTarget/showReorderFulfillmentOptionsSelector';
import { isUserSDUSubscriberSelector } from 'viewModel/selectors/user/isUserSDUSubscriberSelector';
import { isUserSDUTrialEligibleSelector } from 'viewModel/selectors/user/isUserSDUTrialEligibleSelector';
import { showAddDeliveryPriceToSDDSelector } from 'viewModel/selectors/testTarget/showAddDeliveryPriceToSDDSelector';
import { activeFreeShippingCopyPDPSelector } from 'viewModel/selectors/testTarget/activeFreeShippingCopyPDPSelector';
import { showFastShippingCopySignedOutSelector } from 'viewModel/selectors/testTarget/showFastShippingCopySignedOutSelector';
import { showFastShippingCopySignedInSelector } from 'viewModel/selectors/testTarget/showFastShippingCopySignedInSelector';
import { activeSimplifyPickupBopisHeaderTextPdpSelector } from 'viewModel/selectors/testTarget/activeSimplifyPickupBopisHeaderTextPdpSelector';
import { isMultiStoreFilteringEnabledSelector } from 'viewModel/selectors/shopYourStore/isMultiStoreFilteringEnabledSelector';
import { isSDUAddedToBasketSelector } from 'viewModel/selectors/basket/isSDUAddedToBasketSelector';
import { isSameDayCouponAppliedSelector } from 'viewModel/selectors/basket/isSameDayCouponAppliedSelector';
import basketConsts from 'constants/Basket';
import skuUtils from 'utils/Sku';
import stringUtils from 'utils/String';
import localeUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import Location from 'utils/Location';
import urlUtils from 'utils/Url';
import { QUERY_PARAMS } from 'constants/Shared';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import storeUtils from 'utils/Store';
import SameDayUnlimitedBindings from 'analytics/bindingMethods/pages/myAccount/SameDayUnlimitedBindings';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA';

const { userSubscriptionsSelector } = UserSubscriptionsSelector;
const { DELIVERY_OPTIONS } = basketConsts;
const { isNDDEnabled, isNDDNewDeliveryName, getFastDeliveryTileSummarySubtitle } = DeliveryOptionsUtils;
const getText = localeUtils.getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR);

// Helper: Get delivery pricing text for A/B test (TS-4006)
const getDeliveryPricingText = (
    showDeliveryPrice,
    sameDayAvailable,
    currentSku,
    sddTilePromoMessage,
    isUserSDUSubscriber,
    isSDUAddedToBasket,
    isSameDayCouponApplied
) => {
    const displayDeliveryPrice = showDeliveryPrice && sameDayAvailable && currentSku?.isSameDayEligibleSku;

    if (!displayDeliveryPrice) {
        return null;
    }

    const shouldHidePricing = !!sddTilePromoMessage || isUserSDUSubscriber || isSDUAddedToBasket || isSameDayCouponApplied;

    if (shouldHidePricing) {
        return null;
    }

    // Use localized pricing strings based on country
    const isCanada = localeUtils.isCanada();
    const pricingKey = isCanada ? 'sduPricingCA' : 'sduPricingUS';

    return getText(pricingKey);
};

// Helper: derive sameDay subtitle (mirrors sameDaySubTitleSelector combiner)
const computeSameDaySubTitle = (
    product,
    sameDayAvailable,
    isUserSDUSubscriber,
    isUserSDUTrialEligible,
    showDeliveryPrice,
    isSDUAddedToBasket,
    isSameDayCouponApplied
) => {
    const sddTilePromoMessage = product?.currentProductUserSpecificDetails?.sddTilePromoMessage;
    const { currentSku } = product;
    let subtitle = '';

    // A/B Test: Delivery pricing (TS-4006)
    const pricingText = getDeliveryPricingText(
        showDeliveryPrice,
        sameDayAvailable,
        currentSku,
        sddTilePromoMessage,
        isUserSDUSubscriber,
        isSDUAddedToBasket,
        isSameDayCouponApplied
    );

    if (pricingText) {
        return pricingText;
    }

    // Priority 1: Promo message takes precedence (existing logic)
    if (sddTilePromoMessage) {
        const isUserElegibleForSDDPromo = sameDayAvailable && (!isUserSDUSubscriber || isUserSDUTrialEligible);

        if (isUserElegibleForSDDPromo) {
            return sddTilePromoMessage;
        }
    }

    // Priority 2: SLA subtitle (NEW - GUAR-7594)
    // CRITICAL: Only show SLA subtitle when:
    // 1. isNDDEnabled() kill switch is ON
    // 2. User is NOT an active SDU subscriber (or is trial eligible)
    // Per Figma: SDU subscribers see blank tiles in non-promo state
    if (isNDDEnabled()) {
        const isUserElegibleForSLA = sameDayAvailable && (!isUserSDUSubscriber || isUserSDUTrialEligible);

        if (isUserElegibleForSLA) {
            const slaSubtitle = getFastDeliveryTileSummarySubtitle(currentSku, getText);

            if (slaSubtitle) {
                return slaSubtitle;
            }
        }
    }

    // Priority 3: Not available message (existing logic)
    if (!currentSku.isSameDayEligibleSku) {
        subtitle = getText(isNDDEnabled() ? 'fastDeliveryNotAvailable' : 'sameDayDeliveryNotAvailable');
    }

    return subtitle;
};

/**
 * useDeliveryOptionsProps
 *
 * Provides all Redux-derived props and dispatched actions that the
 * DeliveryOptions component needs. Replaces the connect() HOC at
 * viewModel/productPage/deliveryOptions/withDeliveryOptionsProps.js.
 *
 * @param {Object} ownProps - The props DeliveryOptions receives from its parent.
 *                            Must include: currentProduct, sameDayAvailable, serviceUnavailable.
 */
export default function useDeliveryOptionsProps({ currentProduct, sameDayAvailable, serviceUnavailable }) {
    // Redux subscriptions
    const reorderFulfillmentOptions = useSelector(showReorderFulfillmentOptionsSelector);
    const basket = useSelector(basketSelector);
    const isSDUAddedToBasket = useSelector(isSDUAddedToBasketSelector);
    const userSubscriptions = useSelector(userSubscriptionsSelector);
    const preferredStoreInfo = useSelector(preferredStoreInfoSelector);
    const shipToHome = useSelector(shipToHomeSelector);
    const reorderFulfillmentOptionsPdp = useSelector(showReorderFulfillmentOptionsPdpSelector);
    const isFreeShippingCopyPDPActive = useSelector(activeFreeShippingCopyPDPSelector);
    const isMultiStoreFilteringEnabled = useSelector(isMultiStoreFilteringEnabledSelector);
    const showFastShippingCopySignedOut = useSelector(showFastShippingCopySignedOutSelector);
    const showFastShippingCopySignedIn = useSelector(showFastShippingCopySignedInSelector);
    const isSimplifyPickupBopisHeaderTextPdpActive = useSelector(activeSimplifyPickupBopisHeaderTextPdpSelector);
    const isUserSDUSubscriber = useSelector(isUserSDUSubscriberSelector);
    const isUserSDUTrialEligible = useSelector(isUserSDUTrialEligibleSelector);
    const showDeliveryPrice = useSelector(showAddDeliveryPriceToSDDSelector);
    const isSameDayCouponApplied = useSelector(isSameDayCouponAppliedSelector);

    // Dispatched actions
    const triggerModalOpenAnalytics = useCallback(isTrialAlreadyAdded => {
        SameDayUnlimitedBindings.trialModalOpen(isTrialAlreadyAdded);
    }, []);

    // sameDaySubTitle (memoized separately since inputs change independently)
    const sameDaySubTitle = useMemo(
        () =>
            computeSameDaySubTitle(
                currentProduct,
                sameDayAvailable,
                isUserSDUSubscriber,
                isUserSDUTrialEligible,
                showDeliveryPrice,
                isSDUAddedToBasket,
                isSameDayCouponApplied
            ),
        [currentProduct, sameDayAvailable, isUserSDUSubscriber, isUserSDUTrialEligible, showDeliveryPrice, isSDUAddedToBasket, isSameDayCouponApplied]
    );

    // Everything else is a pure derivation of the values above; compute inline.
    const { currentSku } = currentProduct;
    const {
        isSameDayEligibleSku, sameDayTitle, replenishmentAdjuster = '0', acceleratedPromotion, isOutOfStock
    } = currentSku;
    const shouldDisplayEdd = !isOutOfStock;
    const isOnlineOnly = skuUtils.isOnlineOnly(currentSku);
    const isGiftCard = skuUtils.isGiftCard(currentSku);
    const bopisPickupTitle = isSimplifyPickupBopisHeaderTextPdpActive ? getText('freeInStorePickUp') : getText('buyOnlineAndPickUp');
    const bopisTitle = isGiftCard ? getText('availableInAllStores') : isOnlineOnly ? getText('onlineOnly') : bopisPickupTitle;
    const storeName = preferredStoreInfo?.displayName;
    const bopisSubTitle = !currentSku.isPickUpEligibleSku ? getText('pickUpNotOfferedForItem') : storeName;
    const pickupMessage = currentProduct.pickupMessage;

    const hasPickupMessage = Boolean(
        pickupMessage?.includes(getText('readyTomorrow')) ||
            pickupMessage?.includes(getText('readyToday')) ||
            pickupMessage?.includes(getText('getItToday')) ||
            pickupMessage?.includes('recevez aujourd’hui')
    );

    // TS-5020: Check if store is closed
    const closingTime = preferredStoreInfo?.storeHours ? storeUtils.getEffectiveStoreClosingTime(preferredStoreInfo.storeHours) : null;
    const isStoreClosed = !closingTime;

    // TS-5019: Do not show "Ready within 2 hours" for OOS products
    // TS-5020: Do not show "Ready within 2 hours" for closed stores
    const bopisCutoffSubTitle =
        pickupMessage?.includes(getText('readyTomorrow')) || isStoreClosed
            ? getText('getItTomorrow')
            : hasPickupMessage && !isOutOfStock
                ? getText('readyWithinTwoHours')
                : bopisSubTitle;

    const isCustomSets = !!(skuUtils.isCustomSetsSingleSkuProduct(currentProduct) || skuUtils.isCustomSetsGroupedSkuProduct(currentProduct));
    const displayBopis = Sephora.configurationSettings.isBOPISEnabled;
    const shippingMethodNotAvailable =
        isGiftCard || isOnlineOnly || isCustomSets || skuUtils.isGwp(currentSku) || skuUtils.isSample(currentSku) || !isSameDayEligibleSku;
    const sduSubscriptions = userSubscriptions?.filter(subscription => subscription.type === 'SDU');
    const isUserSduTrialEligible = sduSubscriptions.length > 0 ? sduSubscriptions[0]?.isTrialEligible : true;
    const isReplenishmentEligible = currentSku?.isReplenishmentEligible;
    const autoReplenProductEligibility =
        currentProduct?.regularChildSkus?.length > 0
            ? currentProduct?.regularChildSkus.some(element => element?.isReplenishmentEligible)
            : isReplenishmentEligible;
    const isAutoReplenishmentEnabled = Sephora.configurationSettings.isAutoReplenishmentEnabled;
    const isAutoReplenishEmptyHubEnabled = !!Sephora.configurationSettings?.isAutoReplenishEmptyHubEnabled;
    const showAutoReplenishment = autoReplenProductEligibility && isAutoReplenishmentEnabled;
    const isSameDayShippingEnabled = Sephora.configurationSettings.isSameDayShippingEnabled;
    // isNDDNewDeliveryName Feature flag takes precedence over API-provided sameDayTitle
    const sameDayTitleText = isNDDNewDeliveryName() ? getText('fastDelivery') : sameDayTitle || getText('sameDayDelivery');
    const sddRadioButtonDisabled = shippingMethodNotAvailable || serviceUnavailable;
    const getItShippedTitle = shouldDisplayEdd && shipToHome?.shipToHomeTitle ? shipToHome.shipToHomeTitle : getText('getItShippedTitle');
    const getItShippedSubTitle = shouldDisplayEdd && shipToHome?.shipToHomeMessage ? shipToHome.shipToHomeMessage : '';

    // Text resources
    const autoReplenishSubTitle = getText('autoReplenishSubTitle', ['{0}']);
    const autoReplenishTitle = getText('autoReplenishTitle');
    const outOfStockAtStore = vars => getText('outOfStockAtStore', vars);
    const autoReplenishSubTitleText = stringUtils.format(autoReplenishSubTitle, Math.ceil(replenishmentAdjuster));
    const autoReplenishHotDeal = localeUtils.isFrench() ? 'Aubaine Autoprovision' : 'Auto-Replenish Hot Deal';
    const autoReplenishTitleText = acceleratedPromotion ? autoReplenishHotDeal : autoReplenishTitle;
    const subTitleAfterText = acceleratedPromotion ? getText('autoReplenishTermsApply') : '';
    const signInText = getText('signIn');
    const forText = getText('for');
    const forFreeShippingText = showFastShippingCopySignedOut
        ? getText('fastFreeShipping')
        : isSimplifyPickupBopisHeaderTextPdpActive
            ? getText('forFreeShippingSimplified')
            : getText('forFreeShipping');
    const freeShippingOnOrdersText = getText('freeShippingOnOrders');

    const location = Location.getLocation();
    const initialDeliveryOption = (urlUtils.getParamsByName(QUERY_PARAMS.DELIVERY_OPTION, location.search) || [])[0];
    const shouldSelectAutoReplenish =
        isAutoReplenishEmptyHubEnabled && isReplenishmentEligible && initialDeliveryOption === DELIVERY_OPTIONS.AUTO_REPLENISH.toLowerCase();
    const nearbyAvailabilityStatus = currentSku.actionFlags?.nearbyAvailabilityStatus;
    const isInStockAtNearbyStore = ExtraProductDetailsUtils.isinStock(nearbyAvailabilityStatus);
    const isOutOfStockWithNearbyInStockAlternative = isMultiStoreFilteringEnabled && isOutOfStock && isInStockAtNearbyStore;
    const alternativeSubTitle = isOutOfStockWithNearbyInStockAlternative ? outOfStockAtStore([storeName]) : null;

    const shippingTitleKey = showFastShippingCopySignedIn
        ? 'fastFreeShipping'
        : isSimplifyPickupBopisHeaderTextPdpActive
            ? 'freeShippingSimplified'
            : 'freeShipping';
    const standardDeliveryOption = {
        deliveryOption: DELIVERY_OPTIONS.STANDARD,
        iconName: 'truck',
        title: userUtils.isAnonymous() ? getItShippedTitle : getText(shippingTitleKey),
        subTitle: getItShippedSubTitle
    };
    const autoReplenishDeliveryOption = {
        deliveryOption: DELIVERY_OPTIONS.AUTO_REPLENISH,
        iconName: 'autoReplenish',
        title: autoReplenishTitleText,
        subTitle: `${autoReplenishSubTitleText}${subTitleAfterText}`,
        hideDeliveryOption: !showAutoReplenishment,
        bccMediaSpecInfo: 'autoReplenish',
        disabled: !isReplenishmentEligible
    };
    const sameDayDeliveryOption = {
        deliveryOption: DELIVERY_OPTIONS.SAME_DAY,
        title: sameDayTitleText,
        subTitle: sameDaySubTitle,
        enableMarkdownSubtitle: true,
        iconName: 'bag',
        bccMediaSpecInfo: 'sameDay',
        hideDeliveryOption: !isSameDayShippingEnabled,
        disabled: sddRadioButtonDisabled
    };
    const bopisDeliveryOption = {
        deliveryOption: DELIVERY_OPTIONS.PICKUP,
        title: bopisTitle,
        subTitle: currentSku.isPickUpEligibleSku ? bopisCutoffSubTitle : bopisSubTitle,
        desktopSubTitle: bopisSubTitle,
        alternativeSubTitle,
        enableMarkdownSubtitle: isOutOfStockWithNearbyInStockAlternative,
        applyGreen: isOutOfStockWithNearbyInStockAlternative ? false : undefined,
        isOutOfStockWithNearbyInStockAlternative,
        iconName: 'store',
        disabled: !currentSku.isPickUpEligibleSku,
        preferredStoreInfo,
        currentProduct,
        hideDeliveryOption: !displayBopis,
        bccMediaSpecInfo: 'bopisStore'
    };

    const deliveryOptions = [standardDeliveryOption, autoReplenishDeliveryOption, sameDayDeliveryOption];

    if (reorderFulfillmentOptionsPdp) {
        deliveryOptions.unshift(bopisDeliveryOption);
    } else {
        deliveryOptions.push(bopisDeliveryOption);
    }

    if (reorderFulfillmentOptions.challengerOne) {
        deliveryOptions[0] = bopisDeliveryOption;
        deliveryOptions[1] = standardDeliveryOption;
        deliveryOptions[2] = sameDayDeliveryOption;
        deliveryOptions[3] = autoReplenishDeliveryOption;
    }

    if (reorderFulfillmentOptions.challengerTwo) {
        deliveryOptions[0] = standardDeliveryOption;
        deliveryOptions[1] = bopisDeliveryOption;
        deliveryOptions[2] = sameDayDeliveryOption;
        deliveryOptions[3] = autoReplenishDeliveryOption;
    }

    return {
        basket,
        shippingMethodNotAvailable,
        isUserSduTrialEligible,
        sddRadioButtonDisabled,
        deliveryOptions,
        isSDUAddedToBasket,
        hasPickupMessage,
        signInText,
        forText,
        forFreeShippingText,
        freeShippingOnOrdersText,
        isFreeShippingCopyPDPActive,
        showFastShippingCopySignedOut,
        showFastShippingCopySignedIn,
        shouldSelectAutoReplenish,
        initialDeliveryOption,
        reorderFulfillmentOptionsPdp,
        triggerModalOpenAnalytics
    };
}
